/**
 * ViewerToolbar - Professional PDF viewer toolbar
 * Features: Page nav, zoom controls, fit modes, download, search, AI
 */

import React from 'react';
import {
  Box,
  IconButton,
  Slider,
  Typography,
  Select,
  MenuItem,
  Divider,
  Tooltip,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  NavigateBefore as PrevIcon,
  NavigateNext as NextIcon,
  Download as DownloadIcon,
  GridView as GridIcon,
  Fullscreen as FullscreenIcon,
  FitScreen as FitPageIcon,
  WidthFull as FitWidthIcon,
  ZoomIn as ZoomInIcon,
  ZoomOut as ZoomOutIcon,
  Bolt as BoltIcon,
  Search as SearchIcon,
  ListAlt as ListIcon
} from '@mui/icons-material';

interface ViewerToolbarProps {
  currentPage: number;
  totalPages: number;
  zoom: number;
  fitMode: 'width' | 'page' | 'none';
  viewMode: 'pdf' | 'grid' | 'liquid';
  pdfUrl: string;
  filename: string;
  onPageChange: (page: number) => void;
  onZoomChange: (zoom: number) => void;
  onFitModeChange: (mode: 'width' | 'page' | 'none') => void;
  onViewModeChange: (mode: 'pdf' | 'grid' | 'liquid') => void;
  onSearchOpen: () => void;
  onAIPanelOpen: () => void;
  onFullscreen: () => void;
  onTOCOpen?: () => void;
}

const ViewerToolbar: React.FC<ViewerToolbarProps> = ({
  currentPage,
  totalPages,
  zoom,
  fitMode,
  viewMode,
  pdfUrl,
  filename,
  onPageChange,
  onZoomChange,
  onFitModeChange,
  onViewModeChange,
  onSearchOpen,
  onAIPanelOpen,
  onFullscreen,
  onTOCOpen
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Direct PDF download (not print dialog)
  const handleDownload = async () => {
    try {
      const response = await fetch(pdfUrl);
      const blob = await response.blob();
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = filename || 'document.pdf';
      link.click();
      URL.revokeObjectURL(link.href);
    } catch (err) {
      console.error('Download failed:', err);
    }
  };

  // Generate page options for dropdown
  const pageOptions = Array.from({ length: totalPages }, (_, i) => i + 1);

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        p: 1,
        bgcolor: '#1A2341',
        color: 'white',
        borderBottom: '1px solid rgba(255,255,255,0.1)',
        flexWrap: isMobile ? 'wrap' : 'nowrap',
        justifyContent: 'center'
      }}
    >
      {/* Page Navigation */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Tooltip title="Previous Page">
          <span>
            <IconButton
              size="small"
              onClick={() => onPageChange(Math.max(1, currentPage - 1))}
              disabled={currentPage <= 1}
              sx={{ color: 'white' }}
            >
              <PrevIcon />
            </IconButton>
          </span>
        </Tooltip>

        <Select
          value={currentPage}
          onChange={(e) => onPageChange(Number(e.target.value))}
          size="small"
          sx={{
            color: 'white',
            '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.3)' },
            '& .MuiSelect-icon': { color: 'white' },
            minWidth: 70
          }}
        >
          {pageOptions.map((p) => (
            <MenuItem key={p} value={p}>
              {p} / {totalPages}
            </MenuItem>
          ))}
        </Select>

        <Tooltip title="Next Page">
          <span>
            <IconButton
              size="small"
              onClick={() => onPageChange(Math.min(totalPages, currentPage + 1))}
              disabled={currentPage >= totalPages}
              sx={{ color: 'white' }}
            >
              <NextIcon />
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      <Divider orientation="vertical" flexItem sx={{ bgcolor: 'rgba(255,255,255,0.2)', mx: 1 }} />

      {/* Download */}
      <Tooltip title="Download PDF">
        <IconButton size="small" onClick={handleDownload} sx={{ color: 'white' }}>
          <DownloadIcon />
        </IconButton>
      </Tooltip>

      {/* Grid View */}
      <Tooltip title="Grid View">
        <IconButton
          size="small"
          onClick={() => onViewModeChange(viewMode === 'grid' ? 'pdf' : 'grid')}
          sx={{ color: viewMode === 'grid' ? '#4A90E2' : 'white' }}
        >
          <GridIcon />
        </IconButton>
      </Tooltip>

      {/* Fullscreen */}
      <Tooltip title="Fullscreen">
        <IconButton size="small" onClick={onFullscreen} sx={{ color: 'white' }}>
          <FullscreenIcon />
        </IconButton>
      </Tooltip>

      <Divider orientation="vertical" flexItem sx={{ bgcolor: 'rgba(255,255,255,0.2)', mx: 1 }} />

      {/* Fit Controls */}
      <Tooltip title="Fit Width">
        <IconButton
          size="small"
          onClick={() => onFitModeChange(fitMode === 'width' ? 'none' : 'width')}
          sx={{ color: fitMode === 'width' ? '#4A90E2' : 'white' }}
        >
          <FitWidthIcon />
        </IconButton>
      </Tooltip>

      <Tooltip title="Fit Page">
        <IconButton
          size="small"
          onClick={() => onFitModeChange(fitMode === 'page' ? 'none' : 'page')}
          sx={{ color: fitMode === 'page' ? '#4A90E2' : 'white' }}
        >
          <FitPageIcon />
        </IconButton>
      </Tooltip>

      <Divider orientation="vertical" flexItem sx={{ bgcolor: 'rgba(255,255,255,0.2)', mx: 1 }} />

      {/* Zoom Controls */}
      {!isMobile && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 180 }}>
          <Tooltip title="Zoom Out">
            <IconButton
              size="small"
              onClick={() => onZoomChange(Math.max(25, zoom - 25))}
              sx={{ color: 'white' }}
            >
              <ZoomOutIcon />
            </IconButton>
          </Tooltip>

          <Typography sx={{ minWidth: 45, textAlign: 'center', fontSize: 13 }}>
            {zoom}%
          </Typography>

          <Slider
            value={zoom}
            min={25}
            max={400}
            step={25}
            onChange={(_, v) => onZoomChange(v as number)}
            sx={{
              width: 80,
              color: 'white',
              '& .MuiSlider-thumb': { width: 12, height: 12 },
              '& .MuiSlider-rail': { opacity: 0.3 }
            }}
          />

          <Tooltip title="Zoom In">
            <IconButton
              size="small"
              onClick={() => onZoomChange(Math.min(400, zoom + 25))}
              sx={{ color: 'white' }}
            >
              <ZoomInIcon />
            </IconButton>
          </Tooltip>
        </Box>
      )}

      <Divider orientation="vertical" flexItem sx={{ bgcolor: 'rgba(255,255,255,0.2)', mx: 1 }} />

      {/* Lightning AI */}
      <Tooltip title="Lightning AI Q&A">
        <IconButton
          size="small"
          onClick={onAIPanelOpen}
          sx={{
            color: '#FFD700',
            '&:hover': { bgcolor: 'rgba(255,215,0,0.2)' }
          }}
        >
          <BoltIcon />
        </IconButton>
      </Tooltip>

      {/* TOC */}
      {onTOCOpen && (
        <Tooltip title="Table of Contents">
          <IconButton size="small" onClick={onTOCOpen} sx={{ color: 'white' }}>
            <ListIcon />
          </IconButton>
        </Tooltip>
      )}

      {/* Search */}
      <Tooltip title="Search in PDF">
        <IconButton size="small" onClick={onSearchOpen} sx={{ color: 'white' }}>
          <SearchIcon />
        </IconButton>
      </Tooltip>
    </Box>
  );
};

export default ViewerToolbar;
